import { useState, useEffect, useRef, useMemo } from 'react';
import { Search, ChevronLeft, ChevronRight } from 'lucide-react';

import { useWizardProviderSearch, useProviderCities } from '@/api/usePresentationWizard';
import { Select } from '@/components/forms';
import { ProviderCard, ProviderDetailModal } from '@/components/providers';
import { Button } from '@/components/ui';
import { useModalStore } from '@/stores';
import { fromProviderSummary } from '@/types/provider-card';
import styles from './SelectVenueStep.module.css';

const PAGE_SIZE = 5;

interface Props {
  selectedId: string | null;
  city: string;
  onCityChange: (city: string) => void;
  onSelect: (id: string | null) => void;
  onSkip: () => void;
  onBack: () => void;
}

export const SelectVenueStep = ({
  selectedId,
  city,
  onCityChange,
  onSelect,
  onSkip,
  onBack,
}: Props) => {
  const [rawSearch, setRawSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [cityInput, setCityInput] = useState('');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { openModal } = useModalStore();

  const { data: citiesData } = useProviderCities();

  // Pagination state
  const [resultsPage, setResultsPage] = useState(0);
  const [similarPage, setSimilarPage] = useState(0);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(rawSearch.trim());
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [rawSearch]);

  const filters = useMemo(() => {
    const f: Record<string, string> = { service: 'venue-infraestructura' };
    if (city) f.city = city;
    return f;
  }, [city]);

  const { data: searchResult, isFetching } = useWizardProviderSearch(debouncedSearch, filters);

  const cityOptions = useMemo(() => {
    const items = [{ value: '', label: 'All cities' }];
    if (citiesData) {
      citiesData.forEach((c) => items.push({ value: c, label: c }));
    }
    return items;
  }, [citiesData]);

  const results = searchResult?.results ?? [];
  // Only show similar providers that actually offer venue services
  const similar = useMemo(() => {
    const raw = searchResult?.similar ?? [];
    return raw.filter((p) =>
      p.services.some(
        (s) => s.toLowerCase().includes('venue') || s.toLowerCase().includes('infraestructura'),
      ),
    );
  }, [searchResult?.similar]);

  // Reset pages when data changes
  useEffect(() => {
    setResultsPage(0);
  }, [results.length]);
  useEffect(() => {
    setSimilarPage(0);
  }, [similar.length]);

  const hasActiveFilters = Boolean(debouncedSearch || city);

  const viewProviderDetails = (p: (typeof results)[number]) => {
    openModal(
      <ProviderDetailModal
        provider={fromProviderSummary(p)}
        isSelected={selectedId === p.id}
        onToggle={() => onSelect(selectedId === p.id ? null : p.id)}
      />,
    );
  };

  return (
    <div className={styles.container}>
      <button className={styles.backButton} onClick={onBack}>
        &larr; Back
      </button>

      <div className={styles.header}>
        <h2 className={styles.title}>Select Venue (Optional)</h2>
        <p className={styles.subtitle}>
          Choose a venue provider to base the renders on, or skip to use only theme and prompt.
        </p>
      </div>

      <div className={styles.filtersSection}>
        <div className={styles.searchWrapper}>
          <Search size={16} className={styles.searchIcon} />
          <input
            className={styles.searchInput}
            type="text"
            value={rawSearch}
            onChange={(e) => setRawSearch(e.target.value)}
            placeholder="Search venues..."
          />
        </div>
        <div className={styles.filtersRow}>
          <div className={styles.filterItem}>
            <Select
              label=""
              value={city}
              onChange={(e) => onCityChange(e.target.value)}
              options={cityOptions}
              placeholder="Filter by city..."
              size="md"
              allowInput
              inputValue={cityInput}
              onInputChange={setCityInput}
            />
          </div>
        </div>
      </div>

      <div className={styles.resultsSection}>
        {isFetching ? (
          <p className={styles.hint}>Searching...</p>
        ) : (
          <>
            {/* Main results */}
            {results.length > 0 && (
              <div className={styles.resultGroup}>
                <div className={styles.groupHeader}>
                  <h3 className={styles.groupTitle}>
                    {hasActiveFilters ? 'Matching venues' : 'Venues'}
                    <span className={styles.groupCount}>{results.length}</span>
                  </h3>
                  {results.length > PAGE_SIZE && (
                    <div className={styles.pagination}>
                      <button
                        className={styles.pageArrow}
                        onClick={() => setResultsPage((p) => p - 1)}
                        disabled={resultsPage === 0}
                        aria-label="Previous page"
                      >
                        <ChevronLeft size={18} />
                      </button>
                      <span className={styles.pageIndicator}>
                        {resultsPage + 1} / {Math.ceil(results.length / PAGE_SIZE)}
                      </span>
                      <button
                        className={styles.pageArrow}
                        onClick={() => setResultsPage((p) => p + 1)}
                        disabled={resultsPage >= Math.ceil(results.length / PAGE_SIZE) - 1}
                        aria-label="Next page"
                      >
                        <ChevronRight size={18} />
                      </button>
                    </div>
                  )}
                </div>
                <div className={styles.grid}>
                  {results
                    .slice(resultsPage * PAGE_SIZE, (resultsPage + 1) * PAGE_SIZE)
                    .map((p) => (
                      <ProviderCard
                        key={p.id}
                        provider={fromProviderSummary(p)}
                        isSelected={selectedId === p.id}
                        onToggle={() => onSelect(selectedId === p.id ? null : p.id)}
                        onViewDetails={() => viewProviderDetails(p)}
                      />
                    ))}
                </div>
              </div>
            )}

            {/* Similar / suggested */}
            {similar.length > 0 && (
              <div className={styles.resultGroup}>
                <div className={styles.groupHeader}>
                  <h3 className={styles.groupTitle}>
                    {results.length > 0
                      ? 'You might also consider'
                      : hasActiveFilters
                        ? 'Similar venues'
                        : 'Recent venues'}
                    <span className={styles.groupCount}>{similar.length}</span>
                  </h3>
                  {similar.length > PAGE_SIZE && (
                    <div className={styles.pagination}>
                      <button
                        className={styles.pageArrow}
                        onClick={() => setSimilarPage((p) => p - 1)}
                        disabled={similarPage === 0}
                        aria-label="Previous page"
                      >
                        <ChevronLeft size={18} />
                      </button>
                      <span className={styles.pageIndicator}>
                        {similarPage + 1} / {Math.ceil(similar.length / PAGE_SIZE)}
                      </span>
                      <button
                        className={styles.pageArrow}
                        onClick={() => setSimilarPage((p) => p + 1)}
                        disabled={similarPage >= Math.ceil(similar.length / PAGE_SIZE) - 1}
                        aria-label="Next page"
                      >
                        <ChevronRight size={18} />
                      </button>
                    </div>
                  )}
                </div>
                <div className={styles.grid}>
                  {similar
                    .slice(similarPage * PAGE_SIZE, (similarPage + 1) * PAGE_SIZE)
                    .map((p) => (
                      <ProviderCard
                        key={p.id}
                        provider={fromProviderSummary(p)}
                        isSelected={selectedId === p.id}
                        onToggle={() => onSelect(selectedId === p.id ? null : p.id)}
                        onViewDetails={() => viewProviderDetails(p)}
                      />
                    ))}
                </div>
              </div>
            )}

            {results.length === 0 && similar.length === 0 && (
              <p className={styles.noResults}>
                {city
                  ? `No venue providers found in ${city}. Try a different city or remove the filter.`
                  : 'No venue providers found.'}
              </p>
            )}
          </>
        )}
      </div>

      <div className={styles.actions}>
        <Button variant="secondary" onClick={onSkip}>
          Skip &mdash; No Venue
        </Button>
        {selectedId && (
          <Button variant="primary" onClick={() => onSelect(selectedId)}>
            Continue with Selected Venue &rarr;
          </Button>
        )}
      </div>
    </div>
  );
};
